import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {TripOrderByType} from '@di-shared/api/di/type';
import {ERROR} from '@di-shared/const';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {HistoryDateContentProps} from '@di-shared/features/history/date/HistoryDateContent/index';
import useTripDetailEu from '@di-shared/features/history/date/useTripDetail.eu';
import {formatNumberWithLimitAndCommas} from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import useAvailableComponentEu from '@di-shared/features/home/eu/useAvailableComponent.eu';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter, {DistanceUnitSymbol} from '@di-shared/utils/useUnitConverter';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';

export default function ({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
          isMonthChange={isMonthChange}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  const {isEmptyTrip} = useTripDetailEu({vin, selectedDate, orderBy: TripOrderByType.ASC});

  // 월 변경 로딩화면
  if (isMonthChange) {
    return <FallbackLoading />;
  }

  // No data 화면
  if (isEmptyTrip) {
    return <None selectedDate={selectedDate} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function None({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div
          className="trip_date"
          role="text"
          aria-label={`${CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}`}
        >
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('ctn_mytrips_details_today')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_no_driving')}</span>
        </div>
      </div>
    </>
  );
}

function Default({vin, selectedDate}: {vin: string; selectedDate: Date}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const {checkAvailableComponent} = useAvailableComponentEu({vin});
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {tripDetailData, totalDrivingHours, totalDrivingMinutes, formattedTripDetails} = useTripDetailEu({
    vin,
    selectedDate,
    orderBy: TripOrderByType.ASC,
  });

  return (
    <>
      <div className="trip_summary">
        <div className="trip_date">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('ctn_mytrips_details_today')}</span>}
        </div>
        <div className="trip_summary_list type_multi_line">
          <div
            className="trip_summary_item"
            role="text"
          >
            <span className="trip_summary_title">{t('ctn_mytrips_details_distance')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">
                {formatNumberWithLimitAndCommas(convertDistanceValue(tripDetailData.summary.totalDrvDist))}
              </span>
              <span className="trip_summary_unit">{convertDistanceSymbol(tripDetailData.summary.totalDrvDist)}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div
            className="trip_summary_item"
            role="text"
          >
            <span className="trip_summary_title">{t('ctn_mytrips_details_time')}</span>
            <div className="trip_summary_info">
              {!!totalDrivingHours && (
                <>
                  <span className="trip_summary_value">{totalDrivingHours}</span>
                  <span className="trip_summary_unit">{t('ctn_mytrips_details_time_h_01')}</span>
                </>
              )}
              {!!totalDrivingMinutes && (
                <>
                  <span className="trip_summary_value">{totalDrivingMinutes}</span>
                  <span className="trip_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                </>
              )}
              {!totalDrivingHours && !totalDrivingMinutes && <span className="trip_summary_value">{t('imsi_less_minute')}</span>}
            </div>
          </div>
          {checkAvailableComponent('SAFETY_SCORE') && (
            <>
              <div className="divider divider_vertical" />
              <div
                className="trip_summary_item"
                role="text"
              >
                <span className="trip_summary_title">{t('ctn_mytrips_details_driving_safety_score')}</span>
                <div className="trip_summary_info">
                  <span className="trip_summary_value">{formatNumberWithCommas(tripDetailData.summary.safetyDrvScore)}</span>
                  <span className="trip_summary_unit">{t('ctn_mytrips_dss_01')}</span>
                  {tripDetailData.summary.safetyDrvScoreDiff >= 0 ? (
                    <span className="trip_summary_compare type_up">
                      <span
                        className="ic ic_di_count_up"
                        role="img"
                        aria-label={t('ctn_mytrips_recent_rise')}
                      />
                      <span className="trip_summary_value">
                        {formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}
                      </span>
                    </span>
                  ) : (
                    <span className="trip_summary_compare type_down">
                      <span
                        className="ic ic_di_count_down"
                        role="img"
                        aria-label={t('ctn_mytrips_recent_fall')}
                      />
                      <span className="trip_summary_value">
                        {formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {formattedTripDetails.map((trip) => {
        const ignitionOnDate = parseDateWithFormat(trip.ignOnTime);
        const ignitionOffDate = parseDateWithFormat(trip.ignOffTime);

        if (!ignitionOnDate || !ignitionOffDate) {
          throw new Error('ignitionOnDate or ignitionOffDate error');
        }

        return (
          <>
            <div
              className="trip_info"
              key={trip.ignOnTime}
            >
              <div className="trip_info_head">
                <span
                  className="trip_info_item"
                  role="text"
                  aria-label={`${DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')} (${
                    !trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes
                      ? t('imsi_less_minute')
                      : [
                          trip.intervalIgnitionHours && `${trip.intervalIgnitionHours}${t('ctn_mytrips_details_time_h_01')}`,
                          trip.intervalIgnitionMinutes && `${trip.intervalIgnitionMinutes}${t('ctn_mytrips_details_time_min_01')}`,
                        ]
                          .filter(Boolean)
                          .join(' ')
                  })`}
                >
                  {DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}
                  &nbsp;(
                  {!trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes && t('imsi_less_minute')}
                  {!!trip.intervalIgnitionHours && (
                    <>
                      {trip.intervalIgnitionHours}
                      {t('ctn_mytrips_details_time_h_01')}
                    </>
                  )}
                  {!!trip.intervalIgnitionHours && !!trip.intervalIgnitionMinutes && <>&nbsp;</>}
                  {!!trip.intervalIgnitionMinutes && (
                    <>
                      {trip.intervalIgnitionMinutes}
                      {t('ctn_mytrips_details_time_min_01')}
                    </>
                  )}
                  )
                </span>
                {trip.drvDist === 0 ? (
                  <span
                    className="trip_info_item"
                    role="text"
                    aria-label={t('imsi_less_distance_$unit', {
                      unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                    })}
                  >
                    {t('imsi_less_distance_$unit', {
                      unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                    })}
                  </span>
                ) : (
                  <span
                    className="trip_info_item"
                    role="text"
                    aria-label={`${formatNumberWithLimitAndCommas(convertDistanceValue(trip.drvDist))}${convertDistanceSymbol(trip.drvDist)}`}
                  >
                    {formatNumberWithLimitAndCommas(convertDistanceValue(trip.drvDist))}
                    {convertDistanceSymbol(trip.drvDist)}
                  </span>
                )}
              </div>
            </div>
            <ul className="statistics_record_list">
              <li
                className="statistics_record_item"
                role="text"
                aria-label={`${t('ctn_mytrips_details_idle_time')} ${trip.idleTime}${t('ctn_mytrips_details_time_min_01')}`}
              >
                <span className="statistics_record_key">{t('ctn_mytrips_details_idle_time')}</span>
                <span className="statistics_record_val">
                  <span className="statistics_record_number">{trip.idleTime}</span>
                  <span className="statistics_record_unit">{t('ctn_mytrips_details_time_min_01')}</span>
                </span>
              </li>
              <div className="divider divider_vertical" />
              <li
                className="statistics_record_item"
                role="text"
                aria-label={`${t('ctn_mytrips_details_avg_speed')} ${convertSpeedValue(trip.avgSpeed)}${convertSpeedSymbol(trip.avgSpeed)}`}
              >
                <span className="statistics_record_key">{t('ctn_mytrips_details_avg_speed')}</span>
                <span className="statistics_record_val">
                  <span className="statistics_record_number">{convertSpeedValue(trip.avgSpeed)}</span>
                  <span className="statistics_record_unit">{convertSpeedSymbol(trip.avgSpeed)}</span>
                </span>
              </li>
              <div className="divider divider_vertical" />
              <li
                className="statistics_record_item"
                role="text"
                aria-label={`${t('ctn_mytrips_details_top_speed')} ${convertSpeedValue(trip.maxSpeed)}${convertSpeedSymbol(trip.maxSpeed)}`}
              >
                <span className="statistics_record_key">{t('ctn_mytrips_details_top_speed')}</span>
                <span className="statistics_record_val">
                  <span className="statistics_record_number">{convertSpeedValue(trip.maxSpeed)}</span>
                  <span className="statistics_record_unit">{convertSpeedSymbol(trip.maxSpeed)}</span>
                </span>
              </li>
              <div className="divider divider_vertical" />
            </ul>
          </>
        );
      })}
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  const {defaultDistanceSymbol, defaultEnergyEfficiencySymbol, defaultSpeedSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div
        className="trip_summary"
        aria-hidden="true"
      >
        <div className="trip_date skeleton">
          <span className="trip_date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="trip_date_today">{t('ctn_mytrips_details_today')}</span>
        </div>
        <div className="trip_summary_list type_multi_line skeleton">
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_details_distance')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultDistanceSymbol}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_details_time')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('ctn_mytrips_details_time_h_01')}</span>
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{t('ctn_mytrips_details_time_min_01')}</span>
            </div>
          </div>
          <div className="divider divider_vertical" />
          <div className="trip_summary_item">
            <span className="trip_summary_title">{t('ctn_mytrips_details_driving_safety_score')}</span>
            <div className="trip_summary_info">
              <span className="trip_summary_value">{ERROR.DASH}</span>
              <span className="trip_summary_unit">{defaultEnergyEfficiencySymbol}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        className="trip_info skeleton"
        aria-hidden="true"
      >
        <div className="trip_info_head">
          <span className="trip_info_item">{DashedDatetimeFormatter.formatRange(today, today, 'time')}</span>
          <span className="trip_info_item">
            {ERROR.DASH}
            {defaultDistanceSymbol}
          </span>
        </div>
      </div>
      <ul
        className="statistics_record_list skeleton"
        aria-hidden="true"
      >
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('ctn_mytrips_details_idle_time')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{t('ctn_mytrips_details_time_h_01')}</span>
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{t('ctn_mytrips_details_time_min_01')}</span>
          </span>
        </li>
        <div className="divider divider_vertical" />
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('ctn_mytrips_details_avg_speed')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
          </span>
        </li>
        <div className="divider divider_vertical" />
        <li className="statistics_record_item">
          <span className="statistics_record_key">{t('ctn_mytrips_details_top_speed')}</span>
          <span className="statistics_record_val">
            <span className="statistics_record_number">{ERROR.DASH}</span>
            <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
          </span>
        </li>
        <div className="divider divider_vertical" />
      </ul>
    </>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="trip_summary">
        <div
          className="trip_date"
          role="text"
        >
          <span className="trip_date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="trip_date_today">{t('ctn_mytrips_details_today')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_load_data')}</span>
        </div>
      </div>
    </>
  );
}
